import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '../styles/GlobalStyles';
import logoImage from '../assets/image/logo.png';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const HeaderContainer = styled.header`
  background-color: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 0.75rem 0;
  transition: all 0.3s ease;
  
  &.scrolled {
    padding: 0.5rem 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;

  a {
    display: flex;
    align-items: center;
    color: ${primaryColor};
    text-decoration: none;
    font-weight: 600;
    font-size: 1.2rem;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      
      img {
        box-shadow: 0 8px 20px rgba(66, 141, 231, 0.3);
      }
    }
  }
`;

const LogoImage = styled.img`
  height: 50px;
  margin-right: 12px;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    right: 0;
    width: 70%;
    height: 100vh;
    background-color: #fff;
    box-shadow: -2px 0 20px rgba(0, 0, 0, 0.15);
    padding: 2rem;
    transition: all 0.3s ease;
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const NavItem = styled.li`
  margin-left: 1.8rem;
  position: relative;

  @media (max-width: 768px) {
    margin: 1rem 0;
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: #333;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  padding: 5px 0;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 1px;
    background-color: ${primaryColor};
    transition: all 0.3s ease;
    transform: translateX(-50%);
    opacity: 0.7;
  }

  &:hover {
    color: ${primaryColor};
    
    &:after {
      width: 100%;
    }
  }
  
  &.active {
    color: ${primaryColor};
    
    &:after {
      width: 100%;
    }
  }
`;

const DropdownContent = styled.div`
  display: none;
  position: absolute;
  background-color: #fff;
  min-width: 220px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1;
  border-radius: 8px;
  padding: 0.8rem 0;
  transform: translateY(10px);
  opacity: 0;
  transition: all 0.3s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    padding-left: 1rem;
    transform: none;
  }

  ${NavItem}:hover & {
    display: block;
    transform: translateY(0);
    opacity: 1;
  }
`;

const DropdownLink = styled(Link)`
  color: #333;
  padding: 0.6rem 1.2rem;
  display: block;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 1.2rem;
    width: 0;
    height: 1px;
    background-color: ${primaryColor};
    transition: width 0.3s ease;
    opacity: 0.7;
  }

  &:hover {
    background-color: rgba(66, 141, 231, 0.08);
    color: ${primaryColor};
    
    &:after {
      width: calc(100% - 2.4rem);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${primaryColor};
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  right: 1rem;
  transition: color 0.3s ease, transform 0.3s ease;

  &:hover {
    color: ${primaryColor};
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HeaderContainer className={scrolled ? 'scrolled' : ''}>
      <div className="container">
        <HeaderContent>
          <Logo>
            <Link to="/" onClick={closeMenu}>
              <LogoImage src={logoImage} alt="Mission Manager Logo" />
              <span>Mission Manager</span>
            </Link>
          </Logo>
          
          <MobileMenuButton onClick={toggleMenu}>
            <FontAwesomeIcon icon={faBars} />
          </MobileMenuButton>
          
          <Nav isOpen={isMenuOpen}>
            <CloseButton onClick={toggleMenu}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            
            <NavList>
              <NavItem>
                <NavLink to="/" className={location.pathname === '/' ? 'active' : ''} onClick={closeMenu}>
                  {t('header.home')}
                </NavLink>
              </NavItem>
              
              <NavItem>
                <NavLink 
                  to="/products" 
                  className={location.pathname.includes('/products') ? 'active' : ''}
                  onClick={closeMenu}
                >
                  {t('header.products')}
                </NavLink>
                <DropdownContent>
                  <DropdownLink to="/products/mission-manager" onClick={closeMenu}>Mission Manager</DropdownLink>
                </DropdownContent>
              </NavItem>
              
              <NavItem>
                <NavLink 
                  to="/blog" 
                  className={location.pathname.includes('/blog') ? 'active' : ''}
                  onClick={closeMenu}
                >
                  {t('header.blog')}
                </NavLink>
              </NavItem>
              
              <NavItem>
                <NavLink 
                  to="/about" 
                  className={location.pathname.includes('/about') ? 'active' : ''}
                  onClick={closeMenu}
                >
                  {t('header.about')}
                </NavLink>
              </NavItem>
              
              <NavItem>
                <NavLink 
                  to="/contact" 
                  className={location.pathname.includes('/contact') ? 'active' : ''}
                  onClick={closeMenu}
                >
                  {t('header.contact')}
                </NavLink>
              </NavItem>
              <LanguageSwitcher />
            </NavList>
          </Nav>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

export default Header; 